import { bootstrapApplication } from '@angular/platform-browser';
import { appConfig } from './app/app.config';
import { AppComponent } from './app/app.component';
import { SUPPORTED_BROWSERS } from '@frontend/shared';
import * as Sentry from '@sentry/angular';

declare global {
  interface Window {
    initApp: () => void;
  }
  interface Navigator {
    sayswho?: string[];
  }
}

function initApp() {
  bootstrapApplication(AppComponent, appConfig).catch((err) => console.error(err));
}

navigator.sayswho = (function () {
  const ua = navigator.userAgent;
  // Android webview test: Mozilla/5.0 (Linux; Android 16; sdk_gphone64_arm64 Build/BP22.250325.006; wv) AppleWebKit/537.36 (KHTML, like Gecko) Version/4.0 Chrome/133.0.6943.137 Mobile Safari/537.36';
  // iOS webview test: 'Mozilla/5.0 (iPhone; CPU iPhone OS 15_0 like Mac OS X) AppleWebKit/605.1.15 (KHTML, like Gecko) Mobile/15E148oeamtc_hybrid/oeamtc_app_ios/6.8.1-60801004';
  let tem;
  const M = ua.match(/(opera|chrome|safari|firefox|msie|trident(?=\/))\/?\s*(\d+)/i) || [];
  const m1 = M[1] || '';
  const m2 = M[2] || '';

  // OS detection
  let os = '-?';
  if (/android/i.test(ua)) {
    os = 'android';
  } else if (/iPad|iPhone|iPod/.test(ua) || /ios/i.test(ua)) {
    os = 'ios';
  } else if (/Win(dows )?/i.test(ua)) {
    os = 'windows';
  } else if (/Macintosh|Mac OS X/i.test(ua)) {
    os = 'osx';
  } else if (/Linux/i.test(ua)) {
    os = 'linux';
  }

  // Special handling for iOS WebView
  if (os === 'ios' && !ua.includes('Safari/')) {
    // This is iOS WebView - extract iOS version
    const iOSVersion = ua.match(/OS (\d+)_(\d+)/);
    if (iOSVersion && iOSVersion[1]) {
      const majorVersion = iOSVersion[1];
      // Treat WebView as Safari for compatibility purposes
      return ['Safari', majorVersion, os];
    }
    // If we can't extract iOS version, fall through to original logic
  }

  if (/trident/i.test(m1)) {
    tem = /\brv[ :]+(\d+)/g.exec(ua) || [];
    return ['IE', tem[1] ? tem[1] : '', os];
  }
  if (m1 === 'Chrome') {
    tem = ua.match(/\b(OPR)\/(\d+)/);
    if (tem != null) return ['Opera', tem[2] ? tem[2] : '', os];
    tem = ua.match(/\b(Edg[a-zA-Z]{0,3})\/(\d+)/);
    if (tem != null) return ['Edge', tem[2] ? tem[2] : '', os];
  } else if (m1 === 'Safari') {
    tem = ua.match(/\b(EdgiOS)\/(\d+)/);
    if (tem != null) return ['Edge', tem[2] ? tem[2] : '', os];
    tem = ua.match(/\b(FxiOS)\/(\d+)/);
    if (tem != null) return ['Firefox', tem[2] ? tem[2] : '', os];
    tem = ua.match(/\b(CriOS)\/(\d+)/);
    if (tem != null) return ['Chrome', tem[2] ? tem[2] : '', os];
  }
  const safeM1 = m1 ? m1 : navigator.appName;
  const safeM2 = m2 ? m2 : navigator.appVersion;
  const safeM = [safeM1, safeM2, os];

  // Only use Version/ for Safari-based browsers, not for Chrome/WebView
  // WebView has "wv" in user agent and should use the Chrome version number
  const isWebView = /\bwv\b/.test(ua);
  const isChrome = m1 === 'Chrome' || /chrome/i.test(ua);

  if (!isWebView && !isChrome) {
    tem = ua.match(/version\/(\d+)/i);
    if (tem != null && tem[1]) safeM.splice(1, 1, tem[1]);
  }

  return safeM;
})();

if (document.readyState === 'loading') {
  window.addEventListener('DOMContentLoaded', startBrowserCheck);
} else {
  startBrowserCheck();
}

function startBrowserCheck() {
  try {
    const browser = navigator.sayswho;
    console.log('browser', browser);
    if (browser && browser.length > 0) {
      const browserVersion = browser[1];
      const browserName = browser[0]?.toLowerCase();
      const browserOs = browser[2]?.toLowerCase();

      if (browserOs && SUPPORTED_BROWSERS.hasOwnProperty(browserOs)) {
        // Get the browser group (android, ios, desktop)
        const browserGroup = SUPPORTED_BROWSERS[browserOs as keyof typeof SUPPORTED_BROWSERS];
        // Get the minimum supported version for this browser, if it exists
        const minSupportedVersion =
          browserGroup && (browserGroup as Record<string, string>)[browserName ?? ''];
        // Compare as numbers if possible
        if (
          minSupportedVersion &&
          browserVersion &&
          !isNaN(Number(browserVersion)) &&
          !isNaN(Number(minSupportedVersion)) &&
          Number(browserVersion) >= Number(minSupportedVersion)
        ) {
          initApp();
        } else {
          showOutdatedBrowserWarning(browser);
        }
      } else {
        showOutdatedBrowserWarning(browser);
      }
    }
  } catch (error) {
    console.error('Error initializing application:', error);
  }
}

function showOutdatedBrowserWarning(browser: string[]) {
  const warningHtml = `
            <div id="outdated-browser-warning" class="warning-message" aria-live="polite" role="alert"
              style="background-color:#fefce9;color:#ca8a04;border:1px solid #fef08a;border-radius:6px;display:flex;align-items:center;padding:0.5rem 0.625rem;margin:1rem;">
              <div class="warning-message-content">
                <p class="warning-message-title" style="font-weight:600;margin:0 0 0.5rem">Warn Message</p>
                <p class="warning-message-title" style="margin:0 0 1rem">
                  Your browser is outdated, we can't guarantee that the application will work correctly.
                </p>
                <p id="load-anyway-link" style="margin:0;color:blue;cursor:pointer;text-decoration:underline">
                  Click here to load it anyway
                </p>
              </div>
            </div>
          `;
  document.body.insertAdjacentHTML('beforeend', warningHtml);
  console.warn('Browser is outdated:', browser);

  // Add event listener to the load anyway link
  document.getElementById('load-anyway-link')?.addEventListener('click', () => {
    Sentry.captureEvent({
      message: 'User clicked "load anyway" on outdated browser warning',
      level: 'info',
      extra: {
        browser: navigator.sayswho,
      },
    });
    document.getElementById('outdated-browser-warning')?.remove();
    initApp();
  });
}
